import { searchPoster, type Frame } from "../../utils/posterSearch";
import type { UploadPlan } from "../../http/showcase";

/**
 * Sending a sales video's master straight from the browser to R2.
 *
 * The API only presigns: every part goes browser → R2 as a PUT, a few in
 * parallel, each retried on a network error. R2 answers each part with an
 * ETag the API needs to assemble the file, which is why the bucket's CORS
 * policy must EXPOSE `ETag` - without it the browser hides the header and
 * every part looks like it failed.
 *
 * And because the server has no decoder (media-upload-measurement), the
 * browser measures the file and captures a poster before anything is sent.
 */

export const PARALLEL_PARTS = 4;
const PART_RETRIES = 3;

/** Byte range of each part, in plan order. Exported for tests. */
export function partRanges(
  size: number,
  partBytes: number,
  count: number
): { start: number; end: number }[] {
  return Array.from({ length: count }, (_, i) => ({
    start: i * partBytes,
    end: Math.min(size, (i + 1) * partBytes),
  }));
}

export class UploadAbortedError extends Error {
  constructor() {
    super("aborted");
  }
}

function putPart(
  url: string,
  blob: Blob,
  onProgress: (loaded: number) => void,
  signal: AbortSignal
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => {
      const etag = xhr.getResponseHeader("ETag");
      if (xhr.status >= 200 && xhr.status < 300 && etag) resolve(etag);
      else if (xhr.status >= 200 && xhr.status < 300)
        reject(
          new Error(
            "Storage did not expose the part's ETag. The bucket's CORS policy must list ETag under ExposeHeaders."
          )
        );
      else
        reject(
          Object.assign(new Error(`part refused (${xhr.status})`), {
            status: xhr.status,
          })
        );
    };
    xhr.onerror = () => reject(new Error("network"));
    xhr.onabort = () => reject(new UploadAbortedError());
    signal.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(blob);
  });
}

export async function uploadMaster(
  file: File,
  plan: UploadPlan,
  onProgress: (fraction: number) => void,
  signal: AbortSignal
): Promise<{ partNumber: number; etag: string }[]> {
  const ranges = partRanges(file.size, plan.partBytes, plan.parts.length);
  const loaded = new Array<number>(plan.parts.length).fill(0);
  const report = () =>
    onProgress(
      Math.min(1, loaded.reduce((a, b) => a + b, 0) / Math.max(1, file.size))
    );
  const results: { partNumber: number; etag: string }[] = [];
  let next = 0;

  const worker = async () => {
    while (next < plan.parts.length) {
      if (signal.aborted) throw new UploadAbortedError();
      const i = next++;
      const part = plan.parts[i];
      const blob = file.slice(ranges[i].start, ranges[i].end);
      for (let attempt = 0; ; attempt++) {
        try {
          const etag = await putPart(
            part.url,
            blob,
            (n) => {
              loaded[i] = n;
              report();
            },
            signal
          );
          loaded[i] = blob.size;
          report();
          results.push({ partNumber: part.partNumber, etag });
          break;
        } catch (e) {
          const status = (e as { status?: number }).status;
          const refused = status !== undefined && status < 500;
          if (
            e instanceof UploadAbortedError ||
            refused ||
            attempt >= PART_RETRIES
          )
            throw e;
          loaded[i] = 0;
          await new Promise((r) => setTimeout(r, 800 * 2 ** attempt));
        }
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(PARALLEL_PARTS, plan.parts.length) }, worker)
  );
  return results.sort((a, b) => a.partNumber - b.partNumber);
}

export interface Measured {
  durationSeconds: number | null;
  width: number | null;
  height: number | null;
  /** `data:image/jpeg;base64,…`, small enough for the API's poster cap. */
  poster: string | null;
}

/** The API refuses a poster over 200,000 characters. */
const POSTER_MAX_CHARS = 195_000;

/** Draw one frame at a watch-page-worthy size, shrinking until it fits. */
export function grabPoster(video: HTMLVideoElement): Frame | null {
  try {
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return null;
    for (const [edge, quality] of [
      [1280, 0.78],
      [1280, 0.62],
      [960, 0.6],
      [640, 0.6],
    ] as const) {
      const scale = Math.min(1, edge / Math.max(w, h));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(w * scale));
      canvas.height = Math.max(1, Math.round(h * scale));
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/jpeg", quality);
      if (dataUrl.length > POSTER_MAX_CHARS) continue;
      let total = 0;
      let n = 0;
      const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < data.length; i += 4 * 97) {
        total +=
          0.2126 * data[i]! + 0.7152 * data[i + 1]! + 0.0722 * data[i + 2]!;
        n += 1;
      }
      return { dataUrl, luminance: n ? total / n : 0 };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Length, pixel size and a poster, measured in the browser. Every unhappy
 * path resolves with nulls: a file the browser cannot decode (a ProRes .mov
 * in Chrome) still uploads, the farm can read what Chrome cannot.
 */
export function measureMaster(
  file: File,
  timeoutMs = 15_000
): Promise<Measured> {
  const empty: Measured = {
    durationSeconds: null,
    width: null,
    height: null,
    poster: null,
  };
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    let settled = false;
    const done = (m: Measured) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(m);
    };
    const timer = window.setTimeout(() => done(empty), timeoutMs);
    v.preload = "metadata";
    v.muted = true;
    v.playsInline = true;
    v.onerror = () => done(empty);
    v.onloadedmetadata = () => {
      const shape = {
        durationSeconds:
          Number.isFinite(v.duration) && v.duration > 0 ? v.duration : null,
        width: v.videoWidth || null,
        height: v.videoHeight || null,
      };
      const seekTo = (at: number) =>
        new Promise<Frame | null>((settle) => {
          v.onseeked = () => settle(grabPoster(v));
          try {
            v.currentTime = at;
          } catch {
            settle(null);
          }
        });
      void searchPoster(v.duration, seekTo).then((poster) =>
        done({ ...shape, poster })
      );
    };
    v.src = url;
  });
}

/** A poster from wherever a `<video>` element currently stands (frame picker). */
export function posterFromElement(video: HTMLVideoElement): string | null {
  return grabPoster(video)?.dataUrl ?? null;
}
